import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../shared/components/Layout';
import Modal from '../../shared/components/Modal';
import Pagination from '../../shared/components/Pagination';
import TradeDetailModal from './TradeDetailModal';
import { getTrades, forceReleaseTrade } from './api';
import { P2pDispute, PaginatedResponse } from '../../shared/types';
import { useAuth } from '../auth/AuthContext';
import { useUserNames, formatUserLabel } from '../../shared/hooks/useUserNames';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'paid', label: 'Paid' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'disputed', label: 'Disputed' },
];

// Один компонент обслуживает два раздела: P2P (онлайн-оплата) и OTC
// (наличные при встрече) — списки разделены по `mode`, UI у каждого свой.
type Props = { mode: 'p2p' | 'otc' };

const P2P: React.FC<Props> = ({ mode }) => {
  const isOtc = mode === 'otc';
  const sectionLabel = isOtc ? 'OTC' : 'P2P';
  const disputesBase = isOtc ? '/otc/disputes' : '/p2p/disputes';
  const { admin } = useAuth();
  const [trades, setTrades] = useState<PaginatedResponse<P2pDispute> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [userIdInput, setUserIdInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [forceReleaseModalOpen, setForceReleaseModalOpen] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState<P2pDispute | null>(null);

  const [detailTrade, setDetailTrade] = useState<P2pDispute | null>(null);

  const [forceReleaseReason, setForceReleaseReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');

  const canResolve = admin?.backoffice_role === 'manager' || admin?.backoffice_role === 'administrator';
  const canForceRelease = canResolve;

  const loadTrades = useCallback(async (page: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const params: { page: number; per_page: number; status?: string; user_id?: string; mode?: 'p2p' | 'otc' } = {
        page,
        per_page: 20,
      };
      if (statusFilter) params.status = statusFilter;
      params.mode = mode;
      if (userIdFilter.trim()) params.user_id = userIdFilter.trim();
      const data = await getTrades(params);
      setTrades(data);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { status?: number; data?: { error?: string } } }).response?.data?.error ?? 'Failed to load P2P operations'
        : 'Failed to load P2P operations';
      setError(msg);
      setTrades(null);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, mode, userIdFilter]);

  useEffect(() => {
    loadTrades(currentPage);
  }, [currentPage, loadTrades]);

  const applyUserIdFilter = () => {
    setUserIdFilter(userIdInput.trim());
    setCurrentPage(1);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'completed': return 'badge-success';
      case 'cancelled': return 'badge-danger';
      case 'disputed': return 'badge-warning';
      case 'paid': return 'badge-info';
      default: return 'badge-warning';
    }
  };

  const handleForceReleaseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTrade) return;
    setActionError('');
    setIsSubmitting(true);
    try {
      await forceReleaseTrade(selectedTrade.id, { reason: forceReleaseReason || undefined });
      setForceReleaseModalOpen(false);
      setSelectedTrade(null);
      setForceReleaseReason('');
      loadTrades(currentPage);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string; message?: string } } }).response?.data?.error
          ?? (err as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Failed to force release'
        : 'Failed to force release';
      setActionError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openForceReleaseModal = (d: P2pDispute) => {
    setSelectedTrade(d);
    setForceReleaseReason('');
    setActionError('');
    setForceReleaseModalOpen(true);
  };

  const showActions = (d: P2pDispute) =>
    (d.status === 'disputed' || d.status === 'paid') && (canResolve || canForceRelease);

  const partyIds: number[] = [];
  if (trades?.data) {
    for (const d of trades.data) partyIds.push(d.buyer_id, d.seller_id);
  }
  const userNames = useUserNames(partyIds);

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-white">{sectionLabel} Operations</h1>
          <p className="text-slate-400 mt-1">
            {isOtc ? 'All OTC trades (cash in person) — filter by status and user' : 'All P2P trades — filter by status and user'}
          </p>
        </div>

        <div className="card p-4 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="input py-2 w-40"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">User ID (buyer or seller)</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={userIdInput}
                onChange={(e) => setUserIdInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), applyUserIdFilter())}
                placeholder="e.g. 123"
                className="input py-2 w-32"
              />
              <button type="button" onClick={applyUserIdFilter} className="btn btn-secondary py-2">
                Apply
              </button>
              {userIdFilter && (
                <button
                  type="button"
                  onClick={() => { setUserIdInput(''); setUserIdFilter(''); setCurrentPage(1); }}
                  className="btn btn-secondary py-2"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-400">{error}</p>
              {error.includes('Insufficient permissions') && (
                <p className="text-slate-500 mt-2 text-sm">Your role may not have access to P2P.</p>
              )}
            </div>
          ) : !trades?.data.length ? (
            <div className="p-12 text-center text-slate-400">
              No trades found.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="table-header">Trade ID</th>
                      <th className="table-header">Buyer / Seller</th>
                      <th className="table-header">Amount</th>
                      <th className="table-header">Currency</th>
                      <th className="table-header">Status</th>
                      <th className="table-header">Created</th>
                      {(canResolve || canForceRelease) && (
                        <th className="table-header text-right">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {trades.data.map((d) => (
                      <tr
                        key={d.id}
                        className="hover:bg-slate-800/30 transition-colors cursor-pointer"
                        onClick={() => setDetailTrade(d)}
                      >
                        <td className="table-cell font-mono text-indigo-400">
                          #{d.id}
                          {isOtc && d.offer?.meeting_city && (
                            <span className="badge bg-amber-500/20 text-amber-300 ml-2" title="Meeting city">{d.offer.meeting_city}</span>
                          )}
                        </td>
                        <td className="table-cell">
                          <div className="text-sm">
                            <p className="text-emerald-400" title={`#${d.buyer_id}`}>
                              B: {formatUserLabel(d.buyer_id, userNames)}
                            </p>
                            <p className="text-amber-400" title={`#${d.seller_id}`}>
                              S: {formatUserLabel(d.seller_id, userNames)}
                            </p>
                          </div>
                        </td>
                        <td className="table-cell">
                          {d.amount} <span className="text-slate-500">@ {d.price}</span>
                        </td>
                        <td className="table-cell">{d.currency} / {d.fiat_currency}</td>
                        <td className="table-cell">
                          <span className={`badge ${getStatusBadgeClass(d.status)}`}>{d.status}</span>
                        </td>
                        <td className="table-cell text-slate-400">
                          {new Date(d.created_at).toLocaleString()}
                        </td>
                        {(canResolve || canForceRelease) && (
                          <td className="table-cell text-right" onClick={(e) => e.stopPropagation()}>
                            {showActions(d) ? (
                              <div className="flex items-center justify-end gap-2">
                                {d.status === 'disputed' && d.dispute?.id && (
                                  <Link
                                    to={`${disputesBase}/${d.dispute.id}`}
                                    className="px-3 py-1.5 text-xs font-medium text-amber-400 hover:text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 rounded-lg transition-all"
                                  >
                                    Open dispute
                                  </Link>
                                )}
                                <button
                                  onClick={() => openForceReleaseModal(d)}
                                  className="px-3 py-1.5 text-xs font-medium text-emerald-400 hover:text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-lg transition-all"
                                >
                                  Force release
                                </button>
                              </div>
                            ) : (
                              <span className="text-slate-600">—</span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {trades.last_page > 1 && (
                <div className="px-6 py-4 border-t border-slate-800">
                  <Pagination
                    currentPage={currentPage}
                    lastPage={trades.last_page}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Modal
        isOpen={forceReleaseModalOpen}
        onClose={() => { setForceReleaseModalOpen(false); setSelectedTrade(null); }}
        title="Force release"
      >
        <form onSubmit={handleForceReleaseSubmit} className="space-y-4">
          {selectedTrade && (
            <p className="text-slate-400 text-sm">
              Trade #{selectedTrade.id} — funds will be released to buyer.
            </p>
          )}
          {actionError && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {actionError}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Reason (optional)</label>
            <textarea
              value={forceReleaseReason}
              onChange={(e) => setForceReleaseReason(e.target.value)}
              className="input min-h-[80px]"
              rows={3}
              placeholder="Internal note"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => setForceReleaseModalOpen(false)}
              className="flex-1 btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 btn btn-primary disabled:opacity-50"
            >
              {isSubmitting ? 'Releasing...' : 'Force release'}
            </button>
          </div>
        </form>
      </Modal>

      <TradeDetailModal
        isOpen={detailTrade !== null}
        onClose={() => setDetailTrade(null)}
        trade={detailTrade}
      />
    </Layout>
  );
};

export default P2P;
